// Bounded row-query helpers. Decimal values must never pass through Number().
export const FILTER_OPERATORS = Object.freeze(['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'in', 'is_null']);
const TYPES = new Set(['integer', 'float', 'boolean', 'string', 'date', 'datetime']);
const NUMBER_TEXT = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

export function columnLabel(column) {
  const label = column.display_name || column.column_key || column.key;
  return column.unit ? `${label} (${column.unit})` : label;
}

export function resolveDatasetColumns(dataset, response) {
  if (dataset?.schema_id == null || dataset?.schema_version_id == null) {
    throw new Error('Metadata unavailable: dataset has no schema or immutable version reference.');
  }
  const version = response?.schema?.versions?.find(item => String(item.id) === String(dataset.schema_version_id));
  if (!Array.isArray(version?.columns) || !version.columns.length) {
    throw new Error('Metadata unavailable: referenced schema version has no columns.');
  }
  const keys = new Set();
  const columns = version.columns.map(column => {
    if (typeof column.column_key !== 'string' || !column.column_key || keys.has(column.column_key)) {
      throw new Error('Metadata unavailable: missing or duplicate schema column keys.');
    }
    keys.add(column.column_key);
    return { ...column, key: column.column_key };
  });
  return columns.sort((a, b) => (a.ordinal ?? Infinity) - (b.ordinal ?? Infinity));
}

export function operatorsFor(column) {
  if (!column || !TYPES.has(column.logical_type)) return [];
  return column.logical_type === 'boolean'
    ? ['eq', 'ne', 'in', 'is_null'] : [...FILTER_OPERATORS];
}

export function validDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function validDatetime(value) {
  // Preserve the supplied timezone (or absence of one); never append Z.
  if (typeof value !== 'string') return false;
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|([+-])(\d{2}):(\d{2}))?$/.exec(value);
  return !!match && validDate(match[1]) && +match[2] <= 23 && +match[3] <= 59 && +match[4] <= 59
    && (!match[5] || (+match[6] <= 23 && +match[7] <= 59)) && Number.isFinite(Date.parse(value));
}

export function typedFilterValue(type, value) {
  if (type === 'decimal') throw new Error('Decimal filtering is unsupported pending exact decimal serialization.');
  if (type === 'integer' || type === 'float') {
    if (typeof value !== 'number' && (typeof value !== 'string' || !NUMBER_TEXT.test(value.trim()))) {
      throw new Error(`Expected a ${type} value, not an empty, boolean, or non-numeric value.`);
    }
    const number = Number(value);
    if (!Number.isFinite(number) || (type === 'integer' && !Number.isSafeInteger(number))) {
      throw new Error(type === 'integer' ? 'Integer must be within the safe integer range.' : 'Float must be finite.');
    }
    return number;
  }
  if (type === 'boolean') {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    throw new Error('Boolean must be exactly true or false.');
  }
  if (type === 'string' && typeof value === 'string') return value;
  if (type === 'date' && validDate(value)) return value;
  if (type === 'datetime' && validDatetime(value)) return value;
  throw new Error(`Invalid ${type} value. Use ISO dates (YYYY-MM-DD) or ISO datetimes with seconds.`);
}

function freezeDeep(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freezeDeep);
    Object.freeze(value);
  }
  return value;
}

// Draft filters: {column_key, op, value}; `in` accepts an array or JSON-array text.
export function snapshotRowsQuery(columns, projection, filters, limit = 100) {
  const available = new Map(columns.map(column => [column.key, column]));
  if (!Array.isArray(projection) || projection.length < 1 || projection.length > 128
    || new Set(projection).size !== projection.length || projection.some(key => !available.has(key))) {
    throw new Error('Select 1–128 distinct known projection keys.');
  }
  if (!Array.isArray(filters) || filters.length > 32) throw new Error('At most 32 filters are allowed.');
  if (!/^[0-9]+$/.test(String(limit)) || !Number.isInteger(Number(limit)) || Number(limit) < 1 || Number(limit) > 1000) {
    throw new Error('Page limit must be an integer from 1 to 1000.');
  }
  const predicates = filters.map((filter, index) => {
    try {
      const column = available.get(filter.column_key);
      if (column?.logical_type === 'decimal') throw new Error('Decimal filtering is unsupported pending exact decimal serialization.');
      if (!column || !operatorsFor(column).includes(filter.op)) throw new Error('Select a known filter column and an allowed operator.');
      const predicate = { column_key: filter.column_key, op: filter.op };
      if (filter.op === 'is_null') return predicate; // No value field on the wire.
      if (filter.op === 'in') {
        let values = filter.value;
        if (typeof values === 'string') {
          try { values = JSON.parse(values); } catch { throw new Error('IN requires a JSON array.'); }
        }
        if (!Array.isArray(values) || !values.length || values.length > 100) throw new Error('IN requires 1–100 values.');
        predicate.value = values.map(value => typedFilterValue(column.logical_type, value));
      } else {
        predicate.value = typedFilterValue(column.logical_type, filter.value);
      }
      return predicate;
    } catch (error) {
      throw new Error(`Filter ${index + 1}: ${error.message}`);
    }
  });
  return freezeDeep({ columns: [...projection], filters: predicates, limit: Number(limit) });
}

export function rowsQueryPayload(snapshot, cursor) {
  const payload = JSON.parse(JSON.stringify(snapshot));
  if (cursor !== undefined && cursor !== null) payload.cursor = cursor;
  return payload;
}

export function validateRowsPage(page, snapshot) {
  if (!page || !Array.isArray(page.columns) || !Array.isArray(page.rows) || page.rows.length > snapshot.limit
    || typeof page.has_more !== 'boolean' || typeof page.scan_limit_reached !== 'boolean'
    || !Number.isSafeInteger(page.scanned_rows) || page.scanned_rows < 0) {
    throw new Error('Invalid row-query response or page exceeds the requested limit.');
  }
  const keys = page.columns.map(column => column.column_key);
  if (keys.length !== snapshot.columns.length || keys.some((key, index) => key !== snapshot.columns[index])
    || page.rows.some(row => !row || !Number.isSafeInteger(row.ordinal) || !row.values || typeof row.values !== 'object' || Array.isArray(row.values))) {
    throw new Error('Invalid row or response-column metadata; no keys will be inferred.');
  }
  if (page.has_more && page.next_cursor == null) throw new Error('Server reported more rows without a continuation cursor.');
  // The current backend serializer uses display names in values. Normalize once
  // here so tables and charts share stable keys; requests still use column_key.
  const names = page.columns.map(column => column.display_name);
  const unambiguousNames = names.every(name => typeof name === 'string') && new Set(names).size === names.length;
  const rows = page.rows.map(row => {
    // Prefer the stable-key representation, including explicit null/false/zero.
    if (keys.every(key => own(row.values, key))) return row;
    if (!unambiguousNames || !names.every(name => own(row.values, name))) {
      throw new Error('Row values do not contain the projected stable keys or a complete, unique display-name mapping.');
    }
    return {
      ...row,
      values: Object.fromEntries(page.columns.map(column => [column.column_key, row.values[column.display_name]])),
    };
  });
  return { ...page, rows };
}

export function displayRowValue(values, key) {
  if (!values || !own(values, key)) return '(missing)';
  if (values[key] === null) return 'null';
  if (typeof values[key] === 'object') return JSON.stringify(values[key]);
  return String(values[key]); // Retains false, zero, and exact decimal strings.
}

export function chartNumber(value, type) {
  if (!['integer', 'float'].includes(type) || typeof value !== 'number' || !Number.isFinite(value)) return null;
  return type === 'integer' && !Number.isSafeInteger(value) ? null : value;
}

export function chartPoints(rows, xColumn, yColumn) {
  const points = [];
  for (const row of rows) {
    const y = chartNumber(row.values?.[yColumn.column_key], yColumn.logical_type);
    let x;
    if (!xColumn) x = chartNumber(row.ordinal, 'integer');
    else if (['date', 'datetime'].includes(xColumn.logical_type)) {
      const raw = row.values?.[xColumn.column_key];
      const valid = xColumn.logical_type === 'date' ? validDate(raw) : validDatetime(raw);
      x = valid ? Date.parse(raw) : null;
    } else x = chartNumber(row.values?.[xColumn.column_key], xColumn.logical_type);
    if (x != null && y != null && Number.isFinite(x)) points.push({ x, y });
  }
  return points;
}

export function rowsErrorMessage(error) {
  const body = error?.response?.data;
  const status = error?.response?.status;
  const detail = body == null ? error?.message : (typeof body === 'string' ? body : JSON.stringify(body, null, 2));
  return `${status ? `HTTP ${status}: ` : ''}${detail || 'Request failed.'}`;
}